import { useEffect, useState } from 'react';

import { FormikProvider, useFormik } from 'formik';
import { useTranslation } from 'react-i18next';

import { Card, TagList } from '../../../UI';
import { Checkbox, ColorPicker, Input } from '../../../form';
import { DatePicker } from '../../../form';
import RevealButton from '../../../Reveal-button/Reveal-button';
import { useNewStrategyContext } from '../../NewStrategyContext';

import { CURRENCIES, TAG_LIST } from '../../../../constants/lists';
import { useTutorialOverlay } from '../../../../hooks/useTutorialOverlay';
import { useAuth } from '../../../../context/Auth';
import { useUserStrategies } from '../../../../context/UserStrategies';

export const Customize = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [isMoreRevealed, setIsMoreRevealed] = useState(false);
  const { isTooltipIconVisible, newStrategy, dispatch } =
    useNewStrategyContext();

  const { draftStrategy, strategies } = useUserStrategies();

  const tooltipsArray = [...Array(6)].map((_, i) =>
    t(`tooltips.customize.${i}`)
  );

  const isTutorialShown =
    isTooltipIconVisible &&
    (user?.profile?.creationStatus !== 'COMPLETED' ||
      (strategies.length === 0 && !draftStrategy));

  const {
    TutorialOverlay,
    tooltips: [
      TooltipTopCardTitle,
      TooltipStrategyName,
      TooltipCurrency,
      TooltipAdvancedSettings,
      TooltipMiddleCardTitle,
      TooltipBottomCardTitle,
    ],
  } = useTutorialOverlay({
    isTooltipIconVisible,
    isTutorialShown,
    tooltipsArray,
    tooltipClassname: 'absolute -right-6',
    onCloseTutorial: () => dispatch({ isTooltipIconVisible: false }),
  });

  const formik = useFormik({
    initialValues: {
      strategyName: newStrategy.strategyName,
      reportingCurrency: newStrategy.reportingCurrency?.code || '',
      strategyTimeframe: newStrategy.strategyTimeframe,
      timeframeAlert: newStrategy.timeframeAlert,
      strategyColor: newStrategy.strategyColor,
      tagList: newStrategy.tagList,
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      // ...
    },
    validateOnMount: true,
  });

  useEffect(() => {
    const isDisabledNext =
      !formik.values.strategyName.length ||
      !formik.values.reportingCurrency ||
      !formik.values.strategyColor;

    dispatch({
      isDisabledNext,
      newStrategy: {
        ...newStrategy,
        strategyName: formik.values.strategyName,
        reportingCurrency: CURRENCIES.find(
          ({ code }) => code === formik.values.reportingCurrency
        )!,
        strategyTimeframe: formik.values.strategyTimeframe,
        timeframeAlert: formik.values.timeframeAlert,
        strategyColor: formik.values.strategyColor,
        tagList: formik.values.tagList,
      },
    });
  }, [formik.values]);

  const handleDateRangePickerChange = (dates) => {
    const [startDate, endDate] = dates;
    formik.setFieldValue('strategyTimeframe', [startDate, endDate]);
  };

  const handleColorPickerChange = (color) => {
    formik.setFieldValue('strategyColor', color);
  };

  const handleSetSelectedTags = (selectedTags) => {
    formik.setFieldValue('tagList', selectedTags);
  };

  const renderTopCardContent = (): JSX.Element => (
    <div>
      <FormikProvider value={formik}>
        <form autoComplete={'off'}>
          <div className="flex justify-between align-center px-0 lg:px-5 py-5">
            <div className="flex flex-col md:flex-row w-full gap-16">
              <div className="relative flex items-center gap-2 w-full lg:w-1/2">
                <Input
                  name="strategyName"
                  id="strategyName"
                  formik={formik}
                  type="text"
                  placeholder={t(
                    'strategies.newStrategy.customize.strategyName'
                  )}
                  label={t('strategies.newStrategy.customize.strategyName')}
                  validate={(value: string) => {
                    return value?.length === 0;
                  }}
                />
                <TooltipStrategyName />
              </div>
              <div className="relative flex items-center gap-2 w-full lg:w-1/2">
                <Input
                  name="reportingCurrency"
                  id="reportingCurrency"
                  formik={formik}
                  type="dropdown"
                  dropdownItems={CURRENCIES.filter(
                    ({ code }) =>
                      !draftStrategy?.strategyAssets?.items?.length ||
                      code === draftStrategy?.currency
                  ).map(({ code }) => code)}
                  placeholder={t(
                    'strategies.newStrategy.customize.reportingCurrency'
                  )}
                  label={t(
                    'strategies.newStrategy.customize.reportingCurrency'
                  )}
                  validate={(value: string) => {
                    return value?.length === 0;
                  }}
                />
                <TooltipCurrency />
              </div>
            </div>
          </div>
        </form>
      </FormikProvider>
    </div>
  );

  const renderTopAdditionalContent = (): JSX.Element => (
    <>
      <div
        className={`flex flex-col items-center w-full border-t border-grey-10 pt-4 mt-5 dark:border-grey-80 ${
          isMoreRevealed ? '' : 'hidden'
        }`}
      >
        <div className="flex justify-center align-center p-5 w-full lg:w-1/2">
          <div className="relative flex items-center gap-2">
            <p className="font-medium text-sm">
              {t('strategies.newStrategy.customize.chooseTimeframe')}
            </p>
          </div>
        </div>
        <FormikProvider value={formik}>
          <form>
            <div className="flex justify-between align-center pt-5 px-5">
              <div className="flex flex-col items-center">
                <div className="relative flex items-center w-full">
                  <DatePicker
                    startDate={formik.values['strategyTimeframe'][0]}
                    endDate={formik.values['strategyTimeframe'][1]}
                    id={'strategyTimeframe'}
                    onChange={handleDateRangePickerChange}
                    isRangePicker
                  />
                </div>
                <div className="mt-6">
                  <Checkbox
                    id="timeframeAlert"
                    name="timeframeAlert"
                    formik={formik}
                    shape={'square'}
                  >
                    <label
                      htmlFor="timeframeAlert"
                      className="text-sm text-black dark:text-white"
                    >
                      {t('strategies.newStrategy.customize.timeframeAlert')}
                    </label>
                  </Checkbox>
                </div>
              </div>
            </div>
          </form>
        </FormikProvider>
      </div>
      <div className="relative flex justify-end items-center ml-auto mr-8">
        <RevealButton
          size="small"
          revealedLabel={t('strategies.newStrategy.customize.advancedSettings')}
          notRevealedLabel={t(
            'strategies.newStrategy.customize.advancedSettings'
          )}
          setIsMoreRevealed={setIsMoreRevealed}
        />
        <TooltipAdvancedSettings />
      </div>
    </>
  );

  const renderMiddleCardContent = (): JSX.Element => (
    <ColorPicker
      onChange={handleColorPickerChange}
      selectedColor={newStrategy.strategyColor}
    />
  );

  const renderBottomCardContent = (): JSX.Element => (
    <TagList
      tagList={TAG_LIST}
      selectedTagList={newStrategy.tagList}
      setSelectedTagList={handleSetSelectedTags}
    />
  );

  const renderCardContent = ({
    title,
    titleSize,
    tooltip,
    content,
    additionalContent,
    contentWidth = '1/2',
  }: {
    title: string;
    titleSize: string;
    tooltip: JSX.Element;
    content: JSX.Element;
    additionalContent?: JSX.Element;
    contentWidth?: '1/2' | '2/3';
  }) => (
    <div className="flex flex-col w-full items-center">
      <div className="flex justify-center align-center p-5 w-full lg:w-1/2">
        <div className="relative flex items-center gap-2">
          <p className={`font-medium ${titleSize}`}>{title}</p>
          {tooltip}
        </div>
      </div>
      <div
        className={`flex justify-center align-center p-5 w-full lg:w-${contentWidth} overflow-x-auto`}
      >
        <div className="flex flex-col gap-5 w-full">
          <div className="flex flex-col justify-between align-center">
            {content}
          </div>
        </div>
      </div>
      {additionalContent}
    </div>
  );

  return (
    <>
      <TutorialOverlay />
      <div
        className={
          'flex flex-col gap-5 w-full mt-5 px-5 items-center text-black'
        }
      >
        <Card>
          {renderCardContent({
            title: 'Create new strategy',
            tooltip: <TooltipTopCardTitle />,
            titleSize: 'text-2xl',
            content: renderTopCardContent(),
            additionalContent: renderTopAdditionalContent(),
          })}
        </Card>
        <Card>
          {renderCardContent({
            title: t('strategies.newStrategy.customize.chooseStrategyColor'),
            tooltip: <TooltipMiddleCardTitle />,
            titleSize: 'text-sm',
            content: renderMiddleCardContent(),
            contentWidth: '2/3',
          })}
        </Card>
        <Card>
          {renderCardContent({
            title: t('strategies.newStrategy.customize.chooseTags'),
            tooltip: <TooltipBottomCardTitle />,
            titleSize: 'text-sm',
            content: renderBottomCardContent(),
          })}
        </Card>
      </div>
    </>
  );
};
